import { Component, OnInit } from '@angular/core';
import { MediaStats, ReadingStats, UserStatsBundle } from 'src/app/models/statistics';
import { ChartOptions, ChartDataset } from 'chart.js';
import { LocaleService } from 'src/app/services/locale.service';
import { ConfigService } from 'src/app/services/config.service';
import { ThemeService } from 'src/app/services/theme.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerType } from 'src/app/models/client-configuration';
import { BaseChartDirective } from 'ng2-charts';

const range = (start: any, end: any) => Array.from({ length: end - start }, (v, k) => k + start);

@Component({
  selector: 'app-user-stats',
  templateUrl: './user-stats.component.html',
  styleUrls: ['./user-stats.component.scss'],
})
export class UserStatsComponent implements OnInit {
  section = 'stats';

  noStatsAvailable: boolean;

  userStatsBundle: UserStatsBundle;
  readingStats: ReadingStats;
  listeningStats: MediaStats;
  watchingStats: MediaStats;
  scormStats: MediaStats;

  chartColors: any[];
  chartLabels: BaseChartDirective['labels'] = range(0, 24);

  chartReadingData: ChartDataset<'bar'>;
  chartListeningData: ChartDataset<'bar'>;
  chartWatchingData: ChartDataset<'bar'>;
  chartScormingData: ChartDataset<'bar'>;
  chartTotalData: ChartDataset<'bar'>;
  chartData: ChartDataset[] = [];

  chartOptions: ChartOptions;

  minutesHourReading: string;
  minutesHourListening: string;
  minutesHourWatching: string;

  formatEbook: string;
  formatAudio: string;
  formatVideo: string;
  formatScorm: string;
  formatAll: string;

  customerType: CustomerType;

  constructor(
    private localeService: LocaleService,
    private config: ConfigService,
    private themeService: ThemeService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    if (!this.config.get().showStatistics) {
      this.router.navigate(['/user/checkouts']);
      return;
    }

    this.localeService
      .getString(['formats.ebook', 'formats.audio', 'formats.video', 'formats.scorm', 'formats.all'])
      .subscribe((values) => {
        this.formatEbook = values['formats.ebook'];
        this.formatAudio = values['formats.audio'];
        this.formatVideo = values['formats.video'];
        this.formatScorm = values['formats.scorm'];
        this.formatAll = values['formats.all'];
      });

    this.userStatsBundle = this.route.snapshot.data['userStatsBundle'];
    this.customerType = config.customerType;
    this.getStatistics();
  }

  ngOnInit() {}

  private getStatistics() {
    this.readingStats = this.userStatsBundle.readingStats;
    this.listeningStats = this.userStatsBundle.listeningStats;
    this.watchingStats = this.userStatsBundle.watchingStats;
    this.scormStats = this.userStatsBundle.scormStats;

    this.noStatsAvailable =
      this.readingStats.ebookTitle === null &&
      this.listeningStats.title === null &&
      this.watchingStats.title === null &&
      this.scormStats === null;
    const chartTheme = this.themeService.getChartTheme();

    this.chartColors = chartTheme.colors;
    this.chartOptions = chartTheme.options;

    this.computeChartData();
    this.loadChartData('ALL');
  }

  private replaceByZerosIfNull(data: number[]) {
    return data != null ? data : new Array<number>(24).fill(0);
  }

  private computeTotalMinutesOfUsePerHourOfDay(
    read: number[],
    listened: number[],
    watched: number[],
    scormed: number[]
  ) {
    const result = new Array<number>(24).fill(0);
    for (let i = 0; i < read.length; i++) {
      result[i] = read[i] + listened[i] + watched[i] + scormed[i];
    }
    return result;
  }

  private computeChartData() {
    const minutesReadByHourInLast30days = this.replaceByZerosIfNull(this.readingStats.minutesReadByHourInLast30days);
    const minutesListenedByHourInLast30days = this.replaceByZerosIfNull(
      this.listeningStats.minutesPlayedByHourInLast30days
    );
    const minutesWatchedByHourInLast30days = this.replaceByZerosIfNull(
      this.watchingStats.minutesPlayedByHourInLast30days
    );
    const minutesScormedByHourInLast30days = this.replaceByZerosIfNull(this.scormStats.minutesPlayedByHourInLast30days);

    const minutesOfUseByHourInLast30days = this.computeTotalMinutesOfUsePerHourOfDay(
      minutesReadByHourInLast30days,
      minutesListenedByHourInLast30days,
      minutesWatchedByHourInLast30days,
      minutesScormedByHourInLast30days
    );

    this.chartReadingData = {
      data: this.replaceByZerosIfNull(this.readingStats.minutesReadByHourInLast30days),
      label: this.formatEbook,
      backgroundColor: window.getComputedStyle(document.body).getPropertyValue('--primary-accent'),
      hoverBackgroundColor: window.getComputedStyle(document.body).getPropertyValue('--primary-accent'),
    };
    this.chartListeningData = {
      data: this.replaceByZerosIfNull(this.listeningStats.minutesPlayedByHourInLast30days),
      label: this.formatAudio,
      backgroundColor: window.getComputedStyle(document.body).getPropertyValue('--primary-accent'),
      hoverBackgroundColor: window.getComputedStyle(document.body).getPropertyValue('--primary-accent'),
    };
    this.chartWatchingData = {
      data: this.replaceByZerosIfNull(this.watchingStats.minutesPlayedByHourInLast30days),
      label: this.formatVideo,
      backgroundColor: window.getComputedStyle(document.body).getPropertyValue('--primary-accent'),
      hoverBackgroundColor: window.getComputedStyle(document.body).getPropertyValue('--primary-accent'),
    };
    this.chartScormingData = {
      data: this.replaceByZerosIfNull(this.scormStats.minutesPlayedByHourInLast30days),
      label: this.formatScorm,
      backgroundColor: window.getComputedStyle(document.body).getPropertyValue('--primary-accent'),
      hoverBackgroundColor: window.getComputedStyle(document.body).getPropertyValue('--primary-accent'),
    };
    this.chartTotalData = {
      data: minutesOfUseByHourInLast30days,
      label: this.formatAll,
      backgroundColor: window.getComputedStyle(document.body).getPropertyValue('--primary-accent'),
      hoverBackgroundColor: window.getComputedStyle(document.body).getPropertyValue('--primary-accent'),
    };
  }

  loadChartData(format: string) {
    switch (format) {
      case 'EBOOK':
        this.chartData = [this.chartReadingData];
        break;
      case 'AUDIO':
        this.chartData = [this.chartListeningData];
        break;
      case 'VIDEO':
        this.chartData = [this.chartWatchingData];
        break;
      case 'SCORM':
        this.chartData = [this.chartScormingData];
        break;
      default:
        this.chartData = [this.chartTotalData];
    }
  }
}
